import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { isTelegram, isStandalone } from '../detector';

const AuthContext = createContext(null);

// Storage keys for standalone auth
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

// Guest user for unauthenticated standalone access
const GUEST_USER = {
  id: 777000,
  firstName: 'Guest',
  lastName: '',
  username: '',
  photoUrl: '',
  languageCode: 'en',
  isPremium: false,
  allowsWriteToPm: false,
  isGuest: true
};

/**
 * AuthProvider manages authentication for both Telegram WebApp and standalone modes
 */
export function AuthProvider({ children, referer }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Initialize auth based on platform
  useEffect(() => {
    if (isTelegram) {
      initTelegramAuth();
    } else {
      initStandaloneAuth();
    }
  }, []);

  // Telegram WebApp auth - user data is already available
  const initTelegramAuth = () => {
    try {
      const tg = window.Telegram.WebApp;
      const tgUser = tg.initDataUnsafe?.user;
      if (tgUser) {
        setUser({
          id: tgUser.id,
          firstName: tgUser.first_name,
          lastName: tgUser.last_name || '',
          username: tgUser.username || '',
          photoUrl: tgUser.photo_url || '',
          languageCode: tgUser.language_code || 'en',
          isPremium: tgUser.is_premium || false,
          allowsWriteToPm: tgUser.allows_write_to_pm || false
        });
      }
    } catch (e) {
      console.error('Failed to init Telegram auth:', e);
      setAuthError('Failed to initialize Telegram authentication');
    }
    setIsLoading(false);
  };

  // Standalone auth - check for existing session
  const initStandaloneAuth = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const cachedUser = localStorage.getItem(AUTH_USER_KEY);

    if (!token) {
      // No token - use guest mode for unauthenticated browsing
      setUser(GUEST_USER);
      setIsLoading(false);
      return;
    }

    // Try to use cached user while verifying token
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch (e) {
        // Invalid cached user, ignore
      }
    }

    try {
      const response = await fetch('/api/auth/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const verifiedUser = {
          id: data.user.id,
          firstName: data.user.first_name,
          lastName: data.user.last_name || '',
          username: data.user.username || '',
          photoUrl: data.user.photo_url || '',
          languageCode: 'en',
          isPremium: false,
          allowsWriteToPm: true // Always true for standalone
        };
        setUser(verifiedUser);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(verifiedUser));
      } else if (response.status === 401 || response.status === 403) {
        // Token explicitly invalid, clear storage and use guest mode
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
        setUser(GUEST_USER);
      } else {
        // Server error (5xx) or other non-auth error - keep cached user
        console.warn('Auth verify failed with status:', response.status);
        // If we have cached user, keep using it; otherwise use guest
        if (!user) {
          setUser(cachedUser ? JSON.parse(cachedUser) : GUEST_USER);
        }
      }
    } catch (e) {
      console.error('Failed to verify auth token:', e);
      // Keep cached user on network error (backend down, etc.)
      if (!user && cachedUser) {
        try {
          setUser(JSON.parse(cachedUser));
        } catch (parseError) {
          setUser(GUEST_USER);
        }
      }
    }

    setIsLoading(false);
  };

  // Login with Telegram Login Widget data
  const loginWithTelegram = useCallback(async (telegramAuthData) => {
    setIsLoading(true);
    setAuthError(null);

    try {
      const response = await fetch('/api/auth/telegram-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(telegramAuthData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const { token, user: userData } = await response.json();

      const newUser = {
        id: userData.id,
        firstName: userData.first_name,
        lastName: userData.last_name || '',
        username: userData.username || '',
        photoUrl: userData.photo_url || '',
        languageCode: 'en',
        isPremium: false,
        allowsWriteToPm: true
      };

      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(newUser));
      setUser(newUser);
      setIsLoading(false);

      return true;
    } catch (e) {
      console.error('Telegram login failed:', e);
      setAuthError(e.message);
      setIsLoading(false);
      return false;
    }
  }, []);

  // Logout (standalone only) - returns to guest mode
  const logout = useCallback(() => {
    if (isStandalone) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      setUser(GUEST_USER);
    }
  }, []);

  // Build auth headers based on platform
  const getAuthHeaders = useCallback((chatId = '') => {
    // Check initData at runtime (more reliable than cached isTelegram flag)
    const initData = window.Telegram?.WebApp?.initData;
    if (initData && initData !== '') {
      // Telegram WebApp mode
      return {
        'X-WebApp-Hash': initData,
        'X-WebApp-Chat': chatId || '',
        'X-WebApp-Referer': referer || ''
      };
    }

    // Standalone mode - use Bearer token
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'X-WebApp-Referer': referer || ''
      };
    }
    return {};
  }, [referer]);

  // Memoized context value
  const value = useMemo(() => ({
    user,
    isLoading,
    isAuthenticated: !!user && !user.isGuest,
    isGuest: user?.isGuest || false,
    authError,
    loginWithTelegram,
    logout,
    getAuthHeaders,
    isTelegram,
    isStandalone
  }), [user, isLoading, authError, loginWithTelegram, logout, getAuthHeaders]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Component that requires authentication
 * Shows login page for standalone users who aren't logged in
 */
export function RequireAuth({ children, fallback }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return fallback || <div className="auth-loading">Loading...</div>;
  }

  if (!isAuthenticated && isStandalone) {
    // Will be redirected to login by App.js
    return fallback || null;
  }

  return children;
}
